import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel } from '@mui/material';
import { hasPermission } from '../../helpers/Permission';
import { PermissionTypes } from '../../constants';
import { API_CALL } from '../../configs/ActionTypes';
import { enqueueSnackbarCustomMessageHandler } from '../../actions/Status';
import { getSSOValidationError } from '../../helpers/SSOValidation';
import SSOUpload, { SSOFormHandle, FormStateType } from './SSOUploadUSB';

const styles = require('./styles.pcss');

type Props = {
  organizationId: string;
  dispatch: any;
};

type ContainerProps = {
  organizationId: string;
};

export const SSOIntegrationSetup: React.FC<Props> = ({ organizationId, dispatch }) => {
  const permitted = hasPermission('features', PermissionTypes.update);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [enableProvisioning, setEnableProvisioning] = useState<boolean>(false);
  const [enforceRedirect, setEnforceRedirect] = useState<boolean>(false);
  const [disableRequestedAuthnContext, setDisableRequestedAuthnContext] = useState<boolean>(false);
  const [ssoFormHandle, setSsoFormHandle] = useState<SSOFormHandle | null>(null);
  const [initialValues, setInitialValues] = useState<Partial<FormStateType> | undefined>(undefined);
  const [existingIntegrationId, setExistingIntegrationId] = useState<string | null>(null);

  useEffect(() => {
    if (!permitted) return;
    dispatch({
      type: API_CALL,
      payload: {
        call: 'integrations/sso',
        method: 'get',
        data: { organizationId },
        success: (response: any) => {
          const payload = response?.payload || {};
          setExistingIntegrationId(payload._id || payload.id || null);
          const settings = payload.settings || {};
          setEnableProvisioning(Boolean(settings.enableProvisioning));
          setEnforceRedirect(Boolean(settings.enforceRedirect));
          setDisableRequestedAuthnContext(Boolean(settings.disableRequestedAuthnContext));
          setInitialValues({
            entrypoint: settings.entryPoint,
            issuer: settings.issuer,
            idpDisplayName: settings.idpDisplayName,
            cert: settings.cert,
            statusBeforeDeactivation: settings.statusBeforeDeactivation,
            loginRedirectUrl: settings.loginRedirectUrl,
            provider: settings.provider,
            type: settings.type,
            samlIssuer: settings.samlIssuer,
            maxTtl: settings.maxTTL != null ? String(settings.maxTTL) : '',
            status: settings.status,
            enableProvisioning: settings.enableProvisioning,
            enforceRedirect: settings.enforceRedirect,
            disableRequestedAuthnContext: settings.disableRequestedAuthnContext,
          });
        },
        fail: () => {
          // No existing SSO config; form stays with default empty values
        },
      },
    });
  }, [dispatch, organizationId, permitted]);

  const handleEnableProvisioning = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setEnableProvisioning(e.target.checked),
    []
  );

  const handleEnforceRedirect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setEnforceRedirect(e.target.checked),
    []
  );

  const handleDisableRequestedAuthnContext = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setDisableRequestedAuthnContext(e.target.checked),
    []
  );

  const handleSubmit = useCallback(() => {
    if (!ssoFormHandle) {
      enqueueSnackbarCustomMessageHandler(dispatch, 'Form initialization error', 'error');
      return;
    }

    const formData = ssoFormHandle.getFormData();
    const validationError = getSSOValidationError(
      formData.entrypoint,
      formData.issuer,
      formData.cert
    );
    
    if (!ssoFormHandle.validateAndMarkErrors()) {
      const errorMessage = validationError || 'Validation failed';
      enqueueSnackbarCustomMessageHandler(dispatch, errorMessage, 'error');
      return;
    }

    setIsLoading(true);

    const data = {
      organizationId,
      settings: {
        cert: formData.cert,
        entryPoint: formData.entrypoint,
        issuer: formData.issuer,
        enableProvisioning,
        idpDisplayName: formData.idpDisplayName,
        enforceRedirect,
        disableRequestedAuthnContext,
        maxTTL: parseInt(formData.maxTtl, 10),
      },
    };

    const isUpdate = Boolean(existingIntegrationId);
    dispatch({
      type: API_CALL,
      payload: {
        call: isUpdate ? `integrations/${existingIntegrationId}` : 'integrations/sso',
        method: isUpdate ? 'put' : 'post',
        data: JSON.stringify(data),
        success: (response: any) => {
          setIsLoading(false);
          enqueueSnackbarCustomMessageHandler(
            dispatch,
            isUpdate ? 'Integration updated successfully!' : 'Integration added successfully!',
            'success'
          );
          if (!isUpdate && ssoFormHandle?.resetForm) {
            ssoFormHandle.resetForm();
            setEnableProvisioning(false);
            setEnforceRedirect(false);
            setDisableRequestedAuthnContext(false);
          } else {
            const updatedId = response?.payload?._id || response?.payload?.id || existingIntegrationId;
            setExistingIntegrationId(updatedId);
          }
        },
        fail: (error: any) => {
          enqueueSnackbarCustomMessageHandler(dispatch, error?.payload?.message || 'API error occurred', 'error');
          setIsLoading(false);
        },
      },
    });
  }, [dispatch, organizationId, enableProvisioning, enforceRedirect, disableRequestedAuthnContext, ssoFormHandle, existingIntegrationId]);

  if (!permitted) {
    return (
      <Paper sx={{ borderRadius: '8px', marginBottom: '2rem' }}>
        <CardHeader title="Add SAML Integration" />
        <CardContent>
          <Typography color="error">
            Insufficient permissions. Contact your administrator to configure SSO integrations.
          </Typography>
        </CardContent>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        borderRadius: '8px',
        marginBottom: '2rem',
      }}
    >
      <CardHeader
        title={existingIntegrationId ? 'Edit SAML Integration' : 'Add SAML Integration'}
        sx={{
          borderBottom: '1px solid #eee',
          '& .MuiCardHeader-title': {
            fontSize: '1.3rem',
          },
        }}
      />
      <CardContent>
        <SSOUpload onFormReady={setSsoFormHandle} initialValues={initialValues} />
        <FormControlLabel
          control={
            <Checkbox
              checked={enableProvisioning}
              onChange={handleEnableProvisioning}
              color="primary"
            />
          }
          label="Enable Provisioning"
          className={styles.toggle}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={enforceRedirect}
              onChange={handleEnforceRedirect}
              color="primary"
            />
          }
          label="Redirect on TravelBank login (This is off by default and will allow organizations to test their integration, once enabled all users whose email domains match the organization will be redirected to SSO"
          className={styles.toggle}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={disableRequestedAuthnContext}
              onChange={handleDisableRequestedAuthnContext}
              color="primary"
            />
          }
          label="Mobile Override"
          className={styles.toggle}
        />
        <div className={styles.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
            sx={{ position: 'relative' }}
          >
            {isLoading
              ? (existingIntegrationId ? 'Updating Integration...' : 'Adding Integration...')
              : (existingIntegrationId ? 'Update Integration' : 'Add Integration')}
          </Button>
        </div>
      </CardContent>
    </Paper>
  );
};

const SSOIntegrationSetupContainer: React.FC<ContainerProps> = ({ organizationId }) => {
  const dispatch = useDispatch();

  return <SSOIntegrationSetup organizationId={organizationId} dispatch={dispatch} />;
};

export default SSOIntegrationSetupContainer;
